import { useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import { noop } from 'lodash';

import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';

import { drillFragmentData, graphql } from '@e-socna/data-layer';

import { useFolderAccess } from '../../components/Access/hooks/useFolderAccess.hook';
import { Actors as ActorsCmp } from '../../components/Actors';
import { SpeedDial } from '../../components/SpeedDial';
import { FolderActorsFormStep } from '../../forms/Folder/steps/Actors';
import { FolderFormTab } from '../../forms/Folder/enums/tab.enum';
import { useFolderReadyContext } from '../../contexts/FolderContext/hooks/useFolderReadyContext.hook';
import type { ActorPaths } from '../../forms/FolderBusinessEntity/steps/Actors/types/paths.type';

import { FolderPage_Tabs_Folder_Fragment } from './Tabs';

export const FolderPage_Actors_Folder_Fragment = graphql(`
  fragment FolderPage_Actors_Folder_Fragment on Folder {
    _id

    consolidation {
      internalActors {
        ...ActorsCmp_Actor_Fragment
      }
      externalActors {
        ...ActorsCmp_Actor_Fragment
      }
    }

    ...FolderForm_Folder_Fragment
  }
`);

interface Props {
  readonly isInternal: boolean;
  readonly paths?: ActorPaths;
}

export const Actors = ({ isInternal, paths }: Readonly<Props>) => {
  const { folder: rawFolder } = useFolderReadyContext();
  const folder = drillFragmentData(
    [FolderPage_Tabs_Folder_Fragment, FolderPage_Actors_Folder_Fragment],
    rawFolder,
  );

  const { consolidation } = folder;

  const { pathname: currentPath } = useLocation();

  const isInEditionMode = paths && currentPath !== paths.consult;

  const relevantActors = isInternal
    ? consolidation.internalActors
    : consolidation.externalActors;

  const access = useFolderAccess(folder._id, folder);

  return (
    <>
      {!isInEditionMode ? (
        <ActorsCmp
          actionsEnabled={false}
          actors={relevantActors}
          consultThirdPartyInNewTab={false}
          isInternal={isInternal}
        />
      ) : (
        <Formik enableReinitialize initialValues={folder} onSubmit={noop}>
          <FolderActorsFormStep
            id={
              isInternal
                ? FolderFormTab.InternalActors
                : FolderFormTab.ExternalActors
            }
            isInternal={isInternal}
            paths={paths}
            useDirectMutation
          />
        </Formik>
      )}
      {!!paths && (
        <SpeedDial
          actions={
            isInEditionMode
              ? [
                  {
                    id: 'leaveEditionMode',
                    icon: <EditOffIcon />,
                    name: 'Quitter Mode Édition',
                    to: paths.consult,
                  },
                ]
              : [
                  {
                    id: 'editFolderActors',
                    gate: access.update,
                    icon: <EditIcon />,
                    name: `Modifier Acteurs${isInternal ? ' Internes' : ' Externes'}`,
                    to: paths.edit,
                  },
                ]
          }
          title={`Actions possibles sur les Acteurs${isInternal ? ' Internes' : ' Externes'}`}
        />
      )}
    </>
  );
};
